import React from 'react';
import { Table, Pagination, Button, Tooltip } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { Task } from './types';

interface TaskTableProps {
  tasks: Task[];
  loading: boolean;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number, pageSize: number) => void;
  onDownload: (taskId: string) => void;
}

const TaskTable: React.FC<TaskTableProps> = ({
  tasks,
  loading,
  page,
  pageSize,
  total,
  onPageChange,
  onDownload
}) => {
  const formatTime = (timeStr: string) => {
    if (!timeStr) return '-';
    const d = new Date(timeStr);
    return d.toLocaleString();
  };

  const extractFileName = (filePath: string) => {
    if (!filePath) return '-';
    // 获取文件名（最后一个斜杠后的部分）
    const fileName = filePath.split('/').pop() || '';
    // 匹配时间戳格式 YYYYMMDD_HHMMSS_，如果存在则去掉
    const timestampPattern = /^\d{8}_\d{6}_/;
    return fileName.replace(timestampPattern, '');
  };

  const taskColumns = [
    {
      title: '任务ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <span>{text?.slice(0, 10)}{text && text.length > 10 ? '...' : ''}</span>
        </Tooltip>
      )
    },
    {
      title: '项目名称',
      dataIndex: 'project_name',
      key: 'project_name',
      width: 120,
    },
    {
      title: '模板名称',
      dataIndex: 'template_name',
      key: 'template_name',
      width: 150,
    },
    {
      title: '文件名',
      dataIndex: 'file_path',
      key: 'file_path',
      width: 200,
      render: (text: string) => {
        const fileName = extractFileName(text);
        return (
          <Tooltip title={fileName} placement="topLeft">
            <span>{fileName?.length > 30 ? `${fileName.slice(0, 30)}...` : fileName}</span>
          </Tooltip>
        );
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (text: string) => {
        let color = '#999';
        if (text === 'completed') color = '#52c41a';
        if (text === 'processing') color = '#1890ff';
        if (text === 'pending') color = '#faad14';
        if (text === 'failed') color = '#ff4d4f';
        const map: { [key: string]: string } = { 
          completed: '已完成', 
          processing: '处理中', 
          pending: '待处理', 
          failed: '失败' 
        };
        return <span style={{ color }}>{map[text] || text}</span>;
      }
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: formatTime
    },
    {
      title: '完成时间',
      dataIndex: 'completed_at',
      key: 'completed_at',
      width: 160,
      render: formatTime
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_: any, record: Task) => (
        record.status === 'completed' ?
          <Button icon={<DownloadOutlined />} size="small" onClick={() => onDownload(record.id)}>下载</Button>
          : <span style={{ color: '#ccc' }}>-</span>
      )
    },
  ];

  return (
    <>
      <Table
        columns={taskColumns}
        dataSource={tasks}
        pagination={false}
        bordered
        rowKey="id"
        scroll={{ x: 1000 }}
        size="middle"
        loading={loading}
      />
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          current={page}
          total={total}
          pageSize={pageSize}
          showSizeChanger
          pageSizeOptions={["10", "30", "50"]}
          onChange={onPageChange}
          onShowSizeChange={onPageChange}
        />
      </div>
    </>
  );
};

export default TaskTable;
